import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { CheckSquare, ChevronDown, ClipboardList, Edit2, Plus, Star, Trash2, X } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import { Textarea } from "@/clientWeb/components/ui/textarea"
import assetNoSurvey from '@/assets/noSurvey.svg'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import type { StepProps, SurveyData, SurveyQuestion } from "./types"

const QUESTION_TYPES = [
  { value: "short", label: "Short Answer" },
  { value: "singleChoice", label: "Single Choice" },
  { value: "multipleChoice", label: "Multiple Choice" },
  { value: "star", label: "Star Rating" },
]

const emptyQuestion = (): SurveyQuestion => ({
  id: `q-${Date.now()}-${Math.random()}`,
  question: "",
  questionType: "short",
  options: "",
})

const emptySurvey = (): SurveyData => ({
  title: "",
  description: "",
  questions: [emptyQuestion()],
})

function QuestionTypeIcon({ type }: { type: SurveyQuestion["questionType"] }) {
  if (type === "singleChoice") return <ChevronDown className="w-4 h-4 text-blue-500" />
  if (type === "multipleChoice") return <CheckSquare className="w-4 h-4 text-violet-500" />
  if (type === "star") return <Star className="w-4 h-4 text-amber-500" />
  return <ClipboardList className="w-4 h-4 text-slate-400" />
}

function validateSurveyForm(form: SurveyData, t: (key: string) => string) {
  const errs: Record<string, string> = {}
  if (!form.title.trim()) {
    errs.title = t('webinar.validation.surveyTitleRequired')
  } else if (form.title.trim().length < 2) {
    errs.title = t('webinar.validation.surveyTitleMin2')
  } else if (form.title.trim().length > 150) {
    errs.title = t('webinar.validation.surveyTitleMax150')
  }
  if (form.description.length > 500) {
    errs.description = t('webinar.validation.surveyDescriptionMax500')
  }
  const filledQuestions = form.questions.filter((q) => q.question.trim())
  if (filledQuestions.length === 0) {
    errs.questions = t('webinar.validation.surveyMin1Question')
  }

  const qErrs: Record<string, string> = {}
  form.questions.forEach((q) => {
    if (q.question.length > 300) {
      qErrs[q.id] = t('webinar.validation.surveyQuestionMax300')
    } else if ((q.questionType === "singleChoice" || q.questionType === "multipleChoice")) {
      const opts = q.options.split(",").map((o) => o.trim()).filter(Boolean)
      if (opts.length < 2) qErrs[q.id] = t('webinar.validation.surveyOptionsMin2')
    }
  })
  if (Object.keys(qErrs).length) errs._qErrs = JSON.stringify(qErrs)

  return errs
}

export function StepSurveys({ data, onChange }: StepProps) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form, setForm] = useState<SurveyData>(emptySurvey())
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const openCreate = () => {
    setForm(data.survey ? { ...data.survey } : emptySurvey())
    setFormErrors({})
    setOpen(true)
  }

  const handleSave = () => {
    const errs = validateSurveyForm(form, t)
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return }
    onChange({ survey: { ...form, questions: form.questions.filter((q) => q.question.trim()) } })
    setOpen(false)
  }

  const handleDelete = () => {
    onChange({ survey: null })
    setDeleteOpen(false)
  }

  const setQ = (id: string, patch: Partial<SurveyQuestion>) =>
    setForm((p) => ({ ...p, questions: p.questions.map((q) => q.id === id ? { ...q, ...patch } : q) }))

  const addQuestion = () =>
    setForm((p) => ({ ...p, questions: [...p.questions, emptyQuestion()] }))

  const removeQuestion = (id: string) =>
    setForm((p) => ({ ...p, questions: p.questions.filter((q) => q.id !== id) }))

  const survey = data.survey

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.survey")}</p>
          <p className="text-xs text-slate-500 mt-0.5">{t("webinar.collectFeedbackDesc")}</p>
        </div>
        {survey && (
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={openCreate}>
              <Edit2 className="w-4 h-4 mr-1" /> {t("webinar.edit")}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setDeleteOpen(true)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="w-4 h-4 mr-1" /> {t("webinar.delete")}
            </Button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {!survey && (
        <div className="rounded-2xl border-2 border-dashed border-stroke-100 dark:border-border dark:border-border flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="h-24 w-24 rounded-full flex items-center justify-center">
            <img src={assetNoSurvey} alt=""/>
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-white">{t("webinar.noSurveyYet")}</p>
            <p className="text-sm text-slate-500 mt-1">{t("webinar.createFeedbackSurvey")}</p>
          </div>
          <Button type="button" onClick={openCreate} className="mt-1">
            <Plus className="w-4 h-4 mr-2" /> {t("webinar.createSurvey")}
          </Button>
        </div>
      )}

      {/* Survey preview */}
      {survey && (
        <div className="space-y-3">
          <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 bg-slate-50/50 dark:bg-card/30">
            <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white text-base">{survey.title}</p>
            {survey.description && <p className="text-sm text-slate-500 mt-1">{survey.description}</p>}
          </div>

          {survey.questions.map((q, idx) => (
            <div key={q.id} className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-4 bg-card dark:bg-card">
              <div className="flex items-start gap-2">
                <QuestionTypeIcon type={q.questionType} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-800 dark:text-white">
                    <span className="text-slate-400 mr-1">{idx + 1}.</span> {q.question}
                  </p>
                  <span className="text-xs text-slate-400 mt-0.5 block">
                    {QUESTION_TYPES.find((qt) => qt.value === q.questionType)?.label}
                    {(q.questionType === "singleChoice" || q.questionType === "multipleChoice") && q.options && (
                      <> · {q.options.split(",").filter((o) => o.trim()).length} {t("webinar.options")}</>
                    )}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Survey Dialog */}
      <Dialog open={open} onOpenChange={setOpen} size="sm">
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{data.survey ? t("webinar.editSurvey") : t("webinar.createSurvey")}</DialogTitle>
          </DialogHeader>

          {(() => {
            const qErrs: Record<string, string> = formErrors._qErrs ? JSON.parse(formErrors._qErrs) : {}
            return (
              <div className="space-y-4 py-2">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="font-medium text-sm">{t("webinar.surveyTitle")} <span className="text-red-500">*</span></Label>
                    <span className={`text-xs ${form.title.length > 150 ? "text-red-500" : "text-slate-400"}`}>
                      {form.title.length}/150
                    </span>
                  </div>
                  <Input value={form.title}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, title: e.target.value }))
                      setFormErrors((prev) => { const n = { ...prev }; delete n.title; return n })
                    }}
                    placeholder="e.g. Post-Webinar Feedback"
                    className={`h-11 ${formErrors.title ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  {formErrors.title && <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="font-medium text-sm">{t("webinar.description")}</Label>
                    <span className={`text-xs ${form.description.length > 500 ? "text-red-500" : "text-slate-400"}`}>
                      {form.description.length}/500
                    </span>
                  </div>
                  <Textarea value={form.description}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, description: e.target.value }))
                      setFormErrors((prev) => { const n = { ...prev }; delete n.description; return n })
                    }}
                    placeholder="Brief description of the survey" rows={2}
                    className={`resize-none ${formErrors.description ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  {formErrors.description && <p className="text-xs text-red-500 mt-1">{formErrors.description}</p>}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Label className="font-medium text-sm">{t("webinar.questions")}</Label>
                      {formErrors.questions && <p className="text-xs text-red-500">{formErrors.questions}</p>}
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => {
                      addQuestion()
                      setFormErrors((prev) => { const n = { ...prev }; delete n.questions; return n })
                    }} className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> {t("webinar.addQuestion")}
                    </Button>
                  </div>

                  {form.questions.map((q, idx) => (
                    <div key={q.id} className={`rounded-xl border p-4 space-y-3 bg-slate-50/50 dark:bg-card/30 ${qErrs[q.id] ? "border-red-300 dark:border-red-700" : "border-stroke-100 dark:border-border"}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 shrink-0">{idx + 1}.</span>
                        <Input value={q.question}
                          onChange={(e) => {
                            setQ(q.id, { question: e.target.value })
                            setFormErrors((prev) => { const n = { ...prev }; delete n.questions; delete n._qErrs; return n })
                          }}
                          placeholder="Enter question"
                          className={`h-9 flex-1 ${qErrs[q.id] ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                        {form.questions.length > 1 && (
                          <button type="button" onClick={() => removeQuestion(q.id)}
                            className="text-slate-400 hover:text-red-500 transition-colors shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <select value={q.questionType}
                        onChange={(e) => {
                          setQ(q.id, { questionType: e.target.value as SurveyQuestion["questionType"] })
                          setFormErrors((prev) => { const n = { ...prev }; delete n._qErrs; return n })
                        }}
                        className="w-full h-9 rounded-lg border border-stroke-100 dark:border-border bg-card dark:bg-card text-slate-900 dark:text-foreground/80 dark:text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                        {QUESTION_TYPES.map((qt) => <option key={qt.value} value={qt.value}>{qt.label}</option>)}
                      </select>
                      {(q.questionType === "singleChoice" || q.questionType === "multipleChoice") && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs text-slate-500">{t("webinar.optionsCommaSeparated")}</Label>
                            <span className="text-xs text-slate-400">
                              {q.options.split(",").map((o) => o.trim()).filter(Boolean).length} option(s)
                            </span>
                          </div>
                          <Input value={q.options}
                            onChange={(e) => {
                              setQ(q.id, { options: e.target.value })
                              setFormErrors((prev) => { const n = { ...prev }; delete n._qErrs; return n })
                            }}
                            placeholder="Option A, Option B, Option C"
                            className={`h-9 ${qErrs[q.id] ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                          {qErrs[q.id] && <p className="text-xs text-red-500 mt-1">{qErrs[q.id]}</p>}
                        </div>
                      )}
                      {qErrs[q.id] && q.questionType !== "singleChoice" && q.questionType !== "multipleChoice" && (
                        <p className="text-xs text-red-500">{qErrs[q.id]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })()}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("webinar.cancel")}</Button>
            <Button type="button" onClick={handleSave}>
              {data.survey ? t("webinar.saveChanges") : t("webinar.createSurvey")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen} size="xs">
        <DialogContent>
          <DialogHeader><DialogTitle>{t("webinar.deleteSurvey")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300 py-1">
            {t("webinar.confirmDeleteSurvey")}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)}>{t("webinar.cancel")}</Button>
            <Button type="button" variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" /> {t("webinar.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
